import { Component, inject, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { v4 as uuid4 } from 'uuid';

import {
  AnnotationService,
  onCancelAddingAnnotation,
  onPluginReady,
  onCommentsVisibilityChange,
  onConversationPluginReady,
  onMediaDownloadClick,
} from '../../services/annotation.service';
import { AuthService } from '../../services/auth.service';
import { commentFileAdd } from '../../services/comments.service';
import { MtmStompService } from '../../services/mtm-stomp.service';
import {
  PlatformRbacSignatureService,
  SignatureRequestResult,
} from '../../services/platform-rbac-signature.service';
import {
  AiOptions,
  aiParamsFromAnnotationEvent,
  AnnotationPrivateCondition,
  AnnotationSendOtherTypesMessage,
} from '../../interfaces';
import { EmitterService } from '../../services/emitter.service';
import { MTMFileDownloadControl } from '../mtm-file-download/mtm-file-download-control/mtm-file-download-control.component';
import { CommentAnnotationPlugin, Environment, PluginConfig } from '@mtm/conversation-client-sdk-annotation';
import { PlatformCommentHandler } from '../platform-comments-chat/platform-comment-handler';
import { environment } from '../../../../environments/environment';
import { TopMediaInfoService } from '../top-media-info/top-media-info.service';
import { SubsectionService } from '../../services/subsection.service';

@Component({
  selector: 'mtm-comment-annotation',
  styleUrl: './comment-annotation.component.scss',
  templateUrl: './comment-annotation.component.html',
  standalone: false,
})
export class CommentAnnotation implements OnInit, OnDestroy, AfterViewInit {
  @Input() projectId: any;
  @Input() sectionId: any;
  @Input() subsectionId: any;
  @Input() item: any;
  @Input() annotations: any[];
  @Input() participants: any[];
  @Input() hideAnnotationToolbar: boolean = true;
  @Input() hideTopMediaInfo: boolean = false;
  /** Option id for compare control (falls back to `item`). */
  @Input() typeId: string | null = null;
  @Input() instanceId: string;
  @Input() shareLinkPreview: boolean = false;
  annotationPlugin: any;
  commentHandler: PlatformCommentHandler;
  authUser: any;
  annotationComment: string = '';
  pluginReady: any;
  wsId: string;
  newFiles: any = [];
  signature: any;
  files: any = [];
  videoRecordFiles: any = [];
  audioRecordFiles: any = [];
  privateCondition: AnnotationPrivateCondition = null;
  ngUnsubscribe = new Subject();
  isBusy: boolean = false;
  annotatingMode: boolean = false;
  @ViewChild('downloadButton') downloadButton: MTMFileDownloadControl;
  private readonly httpClient = inject(HttpClient);
  private readonly rbacSignature = inject(PlatformRbacSignatureService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly subsectionService = inject(SubsectionService);

  get isFullscreen(): boolean {
    return this.topMediaInfoService.isFullscreen;
  }

  get isCompareMode(): boolean {
    return !!this.subsectionService.uiState?.compareMode;
  }

  constructor(
    private readonly stompService: MtmStompService,
    private readonly authService: AuthService,
    private readonly annotationService: AnnotationService
  ) {}

  private generateSignature() {
    return this.rbacSignature
      .requestSignature('FILE', this.item?.id, this.item?.displayName)
      .pipe(catchError(() => of<SignatureRequestResult | null>(null)));
  }

  private prepareParticipantList() {
    const authUser = this.authService.getAuthUser();
    const inputParticipants = this.participants || [];

    const normalizedAuthEmail = (authUser?.email || '').toLowerCase();
    const hasAuthUser = inputParticipants.some((p) => {
      const email = (p?.email || p?.accountEmail || '').toLowerCase();
      return email && normalizedAuthEmail && email === normalizedAuthEmail;
    });

    const mapped = inputParticipants.map((participant) => ({
      accountEmail: participant?.email || participant?.accountEmail,
      owner: (participant?.email || participant?.accountEmail) === authUser?.email,
    }));

    if (!hasAuthUser && normalizedAuthEmail) {
      mapped.push({
        accountEmail: authUser.email,
        owner: true,
      });
    }

    return mapped;
  }

  ngOnInit(): void {
    this.authUser = this.authService.getAuthUser();
    this.annotationService.privateConditionChange$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (value: AnnotationPrivateCondition) => {
        this.privateCondition = value;
      },
    });
    this.generateSignature().subscribe((result) => {
      this.signature = result?.signature ?? null;
      this.initPlugin();
    });

    EmitterService.get(AnnotationSendOtherTypesMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          if (this.instanceId != data.instanceId) {
            return;
          }
          if (data.message) {
            this.annotationComment = data.message;
            this.newFiles = data.newFiles;
            this.addNewAnnotation(aiParamsFromAnnotationEvent(data));
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.stompService.unsubscribeToService(this.wsId);
  }

  ngAfterViewInit(): void {
    this.wsId = this.stompService.subscribeToService();
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    onCancelAddingAnnotation.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.onCancelAddingAnnotationHandler(args),
    });
    onCommentsVisibilityChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => this.applyAnnotationsDataChange(args),
    });
    // this.stompService.subscribeToListener(
    //   'ANNOTATION_CREATED',
    //   this.wsId,
    //   [this.projectId, this.sectionId, this.subsectionId],
    //   (args: any) => this.wsOnAnnotationCreate(args),
    //   wsListenerANNOTATION_CREATED
    // );

    onMediaDownloadClick.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (args: any) => {
        if (args.instanceId != this.instanceId || args.itemId != this.item?.id) {
          return;
        }
        this.downloadAsset();
      },
    });
  }

  wsOnAnnotationCreate(args) {
    commentFileAdd.emit({
      projectId: this.projectId,
      sectionId: this.sectionId,
      subSectionId: this.subsectionId,
      comment: args.mediaAnnotation.comments[0],
      annotationId: args.mediaAnnotation.id,
      files: this.newFiles,
    });
    this.cleanUpFiles();
  }

  downloadAsset() {
    this.downloadButton.downloadFile();
  }

  addNewAnnotation(aiParams: AiOptions = {}): void {
    if (this.annotationPlugin && this.pluginReady && this.annotationComment) {
      if (this.commentHandler) {
        this.commentHandler.saveAnnotation(this.annotationComment, aiParams);
      }
      this.annotationComment = '';
      this.newFiles = [];
    }
  }

  isInValidInput() {
    return !this.annotationComment;
  }

  initPlugin() {
    const config: PluginConfig = {
      userInfo: this.authUser,
      item: this.item,
      entityName: 'FILE',
      annotationObjects: this.annotations,
      instanceId: this.instanceId,
      itemSignature: this.signature?.access_token,
      httpClient: this.httpClient,
      participants: this.prepareParticipantList(),
      environment: environment.environmentType as Environment,
      ...(this.shareLinkPreview ? { isPrivate: false } : {}),
    };
    this.annotationPlugin = new CommentAnnotationPlugin(config);
    this.annotationPlugin.onReady(() => {
      // specific handler for comment-annotation component behaviors
      this.commentHandler = new PlatformCommentHandler(this.annotationPlugin, this.item, this.instanceId);
      this.pluginReady = true;
      onPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item.id,
        type: 'comment',
      });
      onConversationPluginReady.emit({
        annotationPlugin: this.annotationPlugin,
        instanceId: this.instanceId,
        itemId: this.item?.id,
      });
    });
  }

  onCancelAddingAnnotationHandler(args: any) {
    if (this.instanceId === args.instanceId && this.item.id === args.itemId) {
      this.resetUIComponent();
    }
  }

  startAnnotationMode() {
    if (this.pluginReady && this.annotationPlugin) {
      this.annotatingMode = true;
    }
  }
  resetUIComponent() {
    this.annotationComment = '';
    this.isBusy = false;
    this.annotatingMode = false;
  }

  /** Gets the comment input place holder text depending on general or annot comment */
  getPlaceholderText() {
    // let placeholderText;
    // if ( this.annotationPlugin && this.annotationPlugin.controls && (this.annotationPlugin.controls.uiState.adding || this.annotationPlugin.controls.uiState.editing) ){
    // 	placeholderText = "Write an annotation comment..."
    // }
    // else {
    // 	placeholderText = "Write a general comment..."
    // }

    const placeholderText = 'Write a general comment...';

    return placeholderText;
  }

  cleanUp() {
    this.resetUIComponent();
    this.cleanUpFiles();
  }

  cleanUpFiles() {
    this.newFiles = [];
    this.files = [];
    this.videoRecordFiles = [];
    this.audioRecordFiles = [];
  }

  uploadFileCallback(fileObject) {
    const { files } = fileObject;
    if (!files) {
      return;
    }
    files.map((file) => {
      if (!file.fileLocalId) {
        file.fileLocalId = uuid4();
      }
      return file;
    });
    files.forEach((file) => {
      if (file.method == 'audioRecord') {
        this.audioRecordFiles.push(file);
      } else if (file.method == 'videoRecord') {
        this.videoRecordFiles.push(file);
      } else {
        this.files.push(file);
      }
    });
    this.newFiles.push(...files);
  }

  onFileDeleted(file) {
    this.newFiles = this.newFiles.filter((f) => f.lastModified != file.lastModified);
    this.audioRecordFiles = this.newFiles.filter((f) => f.method == 'audioRecord');
    this.videoRecordFiles = this.newFiles.filter((f) => f.method == 'videoRecord');
    this.files = this.newFiles.filter((f) => !f.method);
  }

  handlePrivateConditionChange(value: AnnotationPrivateCondition) {
    this.annotationService.updatePrivateCondition(value);
  }

  public toggleFullscreenForViewer(): void {
    this.topMediaInfoService.toggleFullscreen();
  }

  applyAnnotationsDataChange(args: any) {
    if (args.data && this.instanceId === args.instanceId && this.item.id === args.itemId) {
      this.annotationPlugin.annotationState.annotations = args.data;
    }
  }
}
